<template>
    <div>
        <rkm-row class="datatable-header">
            <rkm-col :cols="showSortOptions && hasSortOptions ? '6' : '12'">
                <rkm-form-input
                    placeholder="Digite para buscar"
                    v-model="filter"
                >
                    <rkm-input-group-prepend slot="prepend">
                        <rkm-input-group-text>Buscar</rkm-input-group-text>
                    </rkm-input-group-prepend>

                    <rkm-input-group-append slot="append">
                        <rkm-button
                            :disabled="!filter"
                            @click="handleClearFilter"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>

            <template v-if="showSortOptions && hasSortOptions">
                <rkm-col cols="6">
                    <rkm-form-group
                        label-cols="auto"
                        label="Ordenar"
                    >
                        <rkm-form-select
                            :options="sortOptions"
                            show-default-option
                            v-model="localSortBy"
                        >
                            <template #append>
                                <rkm-form-select
                                    :disabled="!localSortBy"
                                    v-model="localSortDesc"
                                >
                                    <rkm-form-select-option :value="false">Asc</rkm-form-select-option>
                                    <rkm-form-select-option :value="true">Desc</rkm-form-select-option>
                                </rkm-form-select>
                            </template>
                        </rkm-form-select>
                    </rkm-form-group>
                </rkm-col>
            </template>
        </rkm-row>

        <slot name="filters" />

        <b-table
            :busy="busy"
            :current-page="currentPage"
            :fields="localFields"
            :filter="filter"
            :items="items"
            :per-page="perPage"
            :sort-by="localSortBy"
            :sort-desc="localSortDesc"
            :striped="striped"
            :tbody-tr-class="tbodyTrClass"
            empty-filtered-text="Não há registros correspondentes à sua solicitação"
            empty-text="Não há registros para exibir"
            hover
            outlined
            ref="table"
            show-empty
            sort-icon-left
        >
            <template v-slot:table-busy>
                <div class="text-center">
                    <rkm-icon icon="circle-notch" loading /> carregando...
                </div>
            </template>

            <template v-slot:empty="scope">
                <div class="text-center">
                    {{ scope.emptyText }}
                </div>
            </template>
            
            <template v-slot:emptyfiltered="scope">
                <div class="text-center">
                    {{ scope.emptyFilteredText }}
                </div>
            </template>

            <template
                v-for="(_, name) in $scopedSlots"
                :slot="name"
                slot-scope="data"
            >
                <slot
                    :name="name"
                    v-bind="data"
                />
            </template>
        </b-table>

        <rkm-row class="datatable-footer">
            <rkm-col>
                <rkm-row class="align-items-center">
                    <rkm-col cols="auto">
                        <b-pagination
                            :per-page="perPage"
                            :total-rows="recordsFiltered"
                            v-model="currentPage"
                        />
                    </rkm-col>

                    <rkm-col
                        v-if="!busy"
                        cols="auto"
                    >
                        <template v-if="recordsFiltered > 0">
                            Exibindo do {{ ((currentPage - 1) * perPage) + 1 }}
                            ao {{ currentPage * perPage }}
                            de {{ recordsFiltered }} registro(s)
                        </template>
                    </rkm-col>
                </rkm-row>
            </rkm-col>

            <rkm-col cols="auto">
                <rkm-form-group
                    label-align="left"
                    label-cols="auto"
                    label="Por página"
                >
                    <rkm-form-select
                        :options="pageOptions"
                        v-model="perPage"
                    />
                </rkm-form-group>
            </rkm-col>
        </rkm-row>
    </div>
</template>

<script>
// Components.
import {BPagination, BTable} from 'bootstrap-vue'

// Mixins.
import {error} from '@/mixins'

export default {
    components: { BPagination, BTable },
    computed: {
        sortOptions() {
            return this.fields.filter(field => !!field.sortable).map(field => {
                return { text: field.label, value: field.key }
            })
        },
        hasSortOptions() {
            return this.sortOptions.length > 0
        },
        localFields() {
            return this.fields.filter(f => (typeof (f.visible) === 'boolean' ? f.visible : true) === true)
        },
    },
    created() {
        this.localSortBy = this.sortBy
        this.localSortDesc = this.sortDesc
    },
    data: () => ({
        busy: false,
        currentPage: 1,
        filter: '',
        localSortBy: null,
        localSortDesc: false,
        pageOptions: [10, 50, 100],
        perPage: 10,
        recordsFiltered: 0,
        recordsTotal: 0,
    }),
    methods: {
        handleClearFilter() {
            this.filter = ''
        },
        items(context) {
            const filter = context.filter.trim()
            const sortBy = context.sortBy.trim()
            const sortDesc = context.sortDesc

            this.busy = true

            const params = {
                columns: this.fields.filter(filter => filter.searchable).map(map => ({
                    data: map.key,
                    name: map.name,
                    orderable: !!map.sortable,
                    searchable: !!map.searchable,
                })),
                draw: filter.length,
                length: context.perPage,
                search: { value: filter },
                start: ((context.currentPage - 1) * context.perPage).toString(),
            }

            if (sortBy) {
                let order = [{
                    column: this.fields.findIndex(field => field.key == sortBy),
                    dir: sortDesc ? 'desc' : 'asc',
                }]

                Object.assign(params, { order })
            }

            return this.repository.datatable({...params, ...this.requestParams})
                .then(response => {
                    const items = response.data

                    this.recordsFiltered = items.recordsFiltered
                    this.recordsTotal = items.recordsTotal
                    this.localSortDesc = sortDesc

                    return items.data
                })
                .catch(this.handleError)
                .finally(() => this.busy = false)
        },
        refresh() {
            this.$refs.table.refresh()
        },
    },
    mixins: [error],
    props: {
        fields: {type: Array, default: null},
        repository: {type: Object, required: true},
        requestParams: {type: Object},
        showSortOptions: {type: Boolean, default: false},
        sortBy: {type: String},
        sortDesc: {type: Boolean},
        striped: {type: Boolean, default: true},
        tbodyTrClass: {type: String},
    },
}
</script>